import { userVar } from 'src/reactive-variables/user'

export interface TenantConfig {
  name: string
  /** API host (no protocol). Falls back to REACT_APP_API_URL when omitted. */
  apiUrl?: string
}

interface TenantsMap {
  [hostname: string]: TenantConfig
}

export const TENANTS: TenantsMap = {
  'app-development.wastehero.io': {
    name: 'WasteHero Development',
  },
  'app-staging.wastehero.io': {
    name: 'WasteHero Staging',
    apiUrl: 'staging-platform-api.wastehero.io',
  },
  'app.wastehero.io': {
    name: 'WasteHero Production',
  },
}

export const DEFAULT_TENANT_KEY = 'app-development.wastehero.io'

export const getTenantConfig = (): TenantConfig =>
  TENANTS[window.location.hostname] ?? TENANTS[DEFAULT_TENANT_KEY]

/**
 * Per-company feature flags (`CompanySettings.*_enabled`, staff-set via
 * Control Center → Feature flags), read off the `me` payload — a non-hook
 * userVar read, same as isHaulerCompany (src/utils/currency.ts).
 *
 * **Every flag fails safe: an absent field reads as NOT granted.** That is the
 * platform convention — a tenant served by an older backend, or whose settings
 * have not loaded yet, must never see a half-built module. There is
 * deliberately no hostname fallback: the per-company value is the only source.
 *
 * The last hostname-hardcoded flags (`sharedServices` /
 * `sharedServicesFinland`) finished migrating here with PRO-522; the backend
 * now serves both fields and existing tenants were backfilled, so the
 * deploy-window fallback they carried has been removed. `TENANTS` survives for
 * `name`/`apiUrl` only (src/api/baseUrl.tsx).
 */
export type TenantFeatureKey =
  | 'legacyContacts'
  | 'standaloneContacts'
  | 'sharedServices'
  | 'sharedServicesFinland'
  | 'wasteFractionLabels'

export const tenantFeature = (feature: TenantFeatureKey): boolean => {
  const companySettings = userVar()?.company?.companySettings
  switch (feature) {
    case 'legacyContacts':
      return Boolean(companySettings?.legacyContactsEnabled)
    case 'standaloneContacts':
      return Boolean(companySettings?.standaloneContactsEnabled)
    // PRO-522: the whole Shared Services module (Customers nav entry, its
    // routes and tabs, the property "Waste arrangement" card, the ticket SCP
    // card). Off also hides the module for tenants that do not use it.
    case 'sharedServices':
      return Boolean(companySettings?.sharedServicesEnabled)
    // PRO-522: the Finland-specific statutory parts (kimppailmoitus,
    // compliance / register basis, municipal radius & caps, authority
    // decisions). Off leaves the generic shared-collection-point core usable
    // outside Finland.
    case 'sharedServicesFinland':
      return Boolean(companySettings?.sharedServicesFinlandEnabled)
    // PRO-143 follow-up: waste-fraction NAMES beside the icons in office list
    // cells. Read here rather than through a hook because the consumers are
    // table-cell render closures, not components
    // (src/api/helpers/wastefraction.tsx).
    case 'wasteFractionLabels':
      return Boolean(companySettings?.wasteFractionLabelsEnabled)
    default:
      return false
  }
}
